import { useEffect, useMemo, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import {
  ShoppingCart,
  People,
  TwoWheeler,
  TrendingUp,
  NightlightRound,
} from "@mui/icons-material";
import { getStatusColor, getStatusIcon } from "../utils/dashboardUtils";
import type { RiderProfile, User } from "../types/firestore";
import { DEFAULT_CONFIG, subscribeToAppConfig } from "../services/configService";
import {
  buildDashboardMetrics,
  buildLaunchNightMetrics,
  buildUsersById,
  subscribeToDashboardOrders,
  subscribeToDashboardRiders,
  subscribeToDashboardUsers,
  subscribeToDiningHallNames,
  type DashboardOrderRow,
} from "../services/dashboardService";

export default function Dashboard() {
  const [orders, setOrders] = useState<DashboardOrderRow[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [riders, setRiders] = useState<RiderProfile[]>([]);
  const [diningHallNames, setDiningHallNames] = useState<Record<string, string>>({});
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  const [ordersReady, setOrdersReady] = useState(false);
  const [usersReady, setUsersReady] = useState(false);
  const [ridersReady, setRidersReady] = useState(false);
  const [hallsReady, setHallsReady] = useState(false);
  const [configReady, setConfigReady] = useState(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToDashboardOrders(
      (nextOrders) => {
        setOrders(nextOrders);
        setOrdersReady(true);
        setError(null);
      },
      (message) => {
        setOrdersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToDashboardUsers(
      (nextUsers) => {
        setUsers(nextUsers);
        setUsersReady(true);
      },
      (message) => {
        setUsersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToDashboardRiders(
      (nextRiders) => {
        setRiders(nextRiders);
        setRidersReady(true);
      },
      (message) => {
        setRidersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToDiningHallNames(
      (nextDiningHallNames) => {
        setDiningHallNames(nextDiningHallNames);
        setHallsReady(true);
      },
      (message) => {
        setHallsReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToAppConfig(
      (nextConfig) => {
        setConfig(nextConfig);
        setConfigReady(true);
      },
      (message) => {
        setConfigReady(true);
        setError(message);
      },
    );
  }, []);

  const loading = !(ordersReady && usersReady && ridersReady && hallsReady && configReady);

  const usersById = useMemo(() => buildUsersById(users), [users]);

  const metrics = useMemo(
    () => buildDashboardMetrics(orders, users, riders),
    [orders, riders, users],
  );
  const launchNight = useMemo(
    () => buildLaunchNightMetrics(orders, riders, config),
    [config, orders, riders],
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Dashboard
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metrics.stats.map((stat) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar sx={{ bgcolor: stat.color, mr: 2 }}>
                    {stat.title === "Today's Orders" ? (
                      <ShoppingCart />
                    ) : stat.title === "Active Riders" ? (
                      <TwoWheeler />
                    ) : stat.title === "Total Students" ? (
                      <People />
                    ) : (
                      <TrendingUp />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" component="div">
                      {loading ? "..." : stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {loading ? "Loading..." : stat.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <NightlightRound color="primary" />
                <Box>
                  <Typography variant="h6">Launch Night Command</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {config.pilotCohortLabel || "Controlled pilot"} guardrails
                  </Typography>
                </Box>
              </Box>
              <Chip
                color={launchNight.readyToOpen ? "success" : "warning"}
                label={launchNight.readyToOpen ? "Ready to open" : "Hold opening"}
              />
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Online riders
                  </Typography>
                  <Typography variant="h5">
                    {loading ? "..." : launchNight.onlineRiders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Minimum {config.minimumOnlineRiders}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Searching orders
                  </Typography>
                  <Typography variant="h5">
                    {loading ? "..." : launchNight.searchingOrders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pause above {config.maxSearchingOrders}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Cap remaining
                  </Typography>
                  <Typography variant="h5">
                    {loading
                      ? "..."
                      : launchNight.capRemaining === null
                        ? "No cap"
                        : launchNight.capRemaining}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed today {launchNight.completedToday}
                  </Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    Active orders
                  </Typography>
                  <Typography variant="h5">
                    {loading ? "..." : launchNight.activeOrders}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pressure {launchNight.pressure}
                  </Typography>
                </CardContent>
              </Card>
            </Stack>

            {launchNight.warnings.length > 0 ? (
              <Alert severity="warning">
                {launchNight.warnings.join(" ")}
              </Alert>
            ) : (
              <Alert severity="success">
                Launch guardrails are clear for the current pilot settings.
              </Alert>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Orders
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : metrics.recentOrders.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>
                No orders found.
              </Typography>
            ) : (
              <List>
                {metrics.recentOrders.map((order) => {
                  const studentUser = usersById[order.data.customerUid];
                  const riderUser = order.data.riderUid
                    ? usersById[order.data.riderUid]
                    : undefined;
                  const studentLabel =
                    studentUser?.displayName || studentUser?.email || "Unknown student";
                  const riderLabel = riderUser?.displayName || "No rider assigned";
                  const createdAt = order.data.createdAt?.toDate?.();
                  const hallLabel =
                    diningHallNames[order.data.diningHallRef] || order.data.diningHallRef;
                  const total =
                    typeof order.data.total === "number"
                      ? order.data.total
                      : (order.data.fee || 0) + (order.data.tip || 0);

                  return (
                    <ListItem key={order.id} divider>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
                          {studentLabel.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="subtitle1">Order #{order.id}</Typography>
                            <Chip
                              label={order.data.status.replace("_", " ")}
                              color={getStatusColor(order.data.status)}
                              size="small"
                              icon={getStatusIcon(order.data.status)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {studentLabel} {" -> "} {riderLabel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {hallLabel} {" | "} {createdAt?.toLocaleString() || "Unknown time"} {" | "} ${total.toFixed(2)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
